import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { Toast } from '../../services/Toast'

const apiUrl = process.env.REACT_APP_API_URL

export default function FilterTemplateScreen() {
  const { userInfo } = useSelector((state) => state.userLogin) || {}
  const [templates, setTemplates] = useState([])
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [loading, setLoading] = useState(true)

  const headers = { Authorization: `Bearer ${userInfo?.token}` }

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/filter-templates`)
      setTemplates(res.data || [])
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  useEffect(() => { fetchTemplates() }, [])

  const handleAdd = async () => {
    if (!newName.trim()) return
    try {
      await axios.post(`${apiUrl}/api/filter-templates`, { name: newName.trim() }, { headers })
      setNewName('')
      fetchTemplates()
      Toast({ type: 'success', message: 'Filter template added' })
    } catch (err) {
      Toast({ type: 'error', message: err.response?.data?.message || 'Failed to add' })
    }
  }

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This won't remove it from existing products.`)) return
    try {
      await axios.delete(`${apiUrl}/api/filter-templates/${id}`, { headers })
      fetchTemplates()
      Toast({ type: 'success', message: 'Deleted' })
    } catch (err) {
      Toast({ type: 'error', message: 'Failed to delete' })
    }
  }

  const handleRename = async (id) => {
    if (!editName.trim()) return
    try {
      await axios.delete(`${apiUrl}/api/filter-templates/${id}`, { headers })
      await axios.post(`${apiUrl}/api/filter-templates`, { name: editName.trim() }, { headers })
      setEditingId(null)
      fetchTemplates()
      Toast({ type: 'success', message: 'Renamed' })
    } catch (err) {
      Toast({ type: 'error', message: err.response?.data?.message || 'Failed to rename' })
    }
  }

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Filter Templates</h4>
      <p className="text-muted mb-3">
        These are the predefined filter names available when adding filter attributes to products.
        Adding a new filter name here makes it available in the dropdown on all product pages.
      </p>

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex gap-2 mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="New filter name (e.g. Processor, Network Type)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              style={{ maxWidth: 400 }}
            />
            <button className="btn btn-success" onClick={handleAdd}>
              Add Template
            </button>
          </div>

          {loading ? (
            <p className="text-muted">Loading...</p>
          ) : templates.length === 0 ? (
            <p className="text-muted">No templates yet. Add one above.</p>
          ) : (
            <table className="table table-sm table-bordered">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Filter Name</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, i) => (
                  <tr key={t._id}>
                    <td>{i + 1}</td>
                    <td>
                      {editingId === t._id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleRename(t._id)}
                          autoFocus
                        />
                      ) : (
                        <strong>{t.name}</strong>
                      )}
                    </td>
                    <td>
                      {editingId === t._id ? (
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-success" onClick={() => handleRename(t._id)}>Save</button>
                          <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => { setEditingId(t._id); setEditName(t.name) }}
                          >
                            Rename
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(t._id, t.name)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
